/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Component, OnInit, OnDestroy } from "@angular/core";
import { AnalyticsService } from "./@core/utils/analytics.service";
import { AuthService } from "./service/auth.service";
import { Router, NavigationEnd } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil, filter } from "rxjs/operators";

@Component({
  selector: "ngx-app",
  template: `
    <div class="app-container">
      <!-- Main content -->
      <div class="main-content">
        <div *ngIf="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading BatteryPool...</p>
        </div>

        <router-outlet *ngIf="!loading"></router-outlet>
      </div>

      <!-- Signout modal (replaces the toast) -->
      <ngx-signout-modal
        *ngIf="currentRoute !== '/auth/login'"
      ></ngx-signout-modal>
    </div>
  `,
  styles: [
    `
      .app-container {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      .main-content {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .loading-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100vh;
        gap: 1rem;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
      }

      .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-top: 4px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .loading-container p {
        margin: 0;
        font-size: 1.1rem;
        opacity: 0.9;
      }

      @media (max-width: 768px) {
        .timer-content {
          padding: 0 1rem;
        }

        .timer-text {
          font-size: 0.75rem;
        }
      }
    `,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  loading = true;
  isAuthenticated = false;
  remainingTime = "";
  showSessionTimer = true;
  private destroy$ = new Subject<void>();
  private updateInterval: any;
  currentRoute: string = "";

  constructor(
    private analytics: AnalyticsService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.analytics.trackPageViews();
    this.initializeApp();
    this.setupSessionMonitoring();
    this.setupRouteMonitoring();

    window.addEventListener('storage', (event) => {
      if (event.key === 'firebase_session_timestamp' || event.key === 'firebase_session_signature') {
        const isLoggedIn = this.authService.loggedIn();
        if (!isLoggedIn) {
          console.log('Detected logout in another tab. Redirecting to login...');
          this.router.navigate(['/auth/login']);
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  private initializeApp() {
    this.authService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      setTimeout(() => {
        this.loading = false;
      }, 500);

      this.isAuthenticated = !!user && this.authService.loggedIn();
    });
  }

  private setupSessionMonitoring() {
    this.updateInterval = setInterval(() => {
      if (this.isAuthenticated) {
        this.remainingTime = this.authService.formatRemainingTime();

        const remaining = this.authService.getRemainingSessionTime();
        if (remaining <= 10000) {
          this.showSessionTimer = false;
        }
      }
    }, 5000);

    setTimeout(() => {
      if (this.isAuthenticated) {
        this.remainingTime = this.authService.formatRemainingTime();
      }
    }, 1000);
  }

  private setupRouteMonitoring() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;

        if (this.isAuthenticated) {
          this.remainingTime = this.authService.formatRemainingTime();
        }
      });
  }

  hideSessionTimer() {
    this.showSessionTimer = false;
  }
}
